import React, { useState } from "react";

import { IDog, IDogRun } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import DatePick from "../../../components/DatePick/DatePick";
import Dropdown from "../../../components/Dropdown/Dropdown";
import Input from "../../../components/Input/Input";
import TextArea from "../../../components/TextArea/TextArea";

import { formatRunDate, parseRunDate, runOption, runOptions } from "./runFormFields";

import "./FetchRunForm.scss";

interface IProps {
  dog: IDog;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun;
}

const LEVELS = [
  { value: Constants.dog_class_element_level.fetch_novice, label: 'Novice' },
  { value: Constants.dog_class_element_level.fetch_intermediate, label: 'Intermediate' },
  { value: Constants.dog_class_element_level.fetch_advanced, label: 'Advanced' },
  { value: Constants.dog_class_element_level.fetch_retriever, label: 'Retriever' }
];

const RESULT_STATUSES = [
  { value: Constants.run_result_status.qualified, label: 'Q (Qualified)' },
  { value: Constants.run_result_status.not_qualified, label: 'NQ (Not Qualified)' },
  { value: Constants.run_result_status.absent, label: 'ABS (Absent)' }
];

const FetchRunForm: React.FC<IProps> = ({ dog, onSubmit, onCancel, isSubmitting, editRun }) => {
  // Form fields
  const [trialDate, setTrialDate] = useState<string>(
    editRun?.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : ''
  );
  const [level, setLevel] = useState<string>(editRun?.level || '');
  const [resultStatus, setResultStatus] = useState<string>(editRun?.result_status || '');
  const [judgeNumber, setJudgeNumber] = useState<string>(editRun?.judge_number || '');
  const [judgeName, setJudgeName] = useState<string>(editRun?.judge_name || '');
  const [eventName, setEventName] = useState<string>(editRun?.event_name || '');
  const [city, setCity] = useState<string>(editRun?.location?.city || '');
  const [state, setState] = useState<string>(editRun?.location?.state || '');
  const [notes, setNotes] = useState<string>(editRun?.notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);
    
    const data: any = {
      dogId: dog._id,
      level,
      resultStatus,
      trialDate: localDate,
      judgeNumber: judgeNumber || undefined,
      judgeName: judgeName || undefined,
      eventName: eventName || undefined,
      location: (city || state) ? { city, state } : undefined,
      notes: notes || undefined
    };

    if (editRun) {
      data.run_id = editRun._id;
    }

    onSubmit(data);
  };

  const levelOptions = runOptions(LEVELS, "Select Level");
  const resultOptions = runOptions(RESULT_STATUSES, "Select Result");
  const isValid = trialDate && level && resultStatus;

  return (
    <form onSubmit={handleSubmit} className="fetchRunForm">
      <div className="formGrid">
        <div className="fieldGroup">
          <DatePick
            label="Trial Date *"
            placeholder="Select date"
            value={parseRunDate(trialDate)}
            onChange={(date: Date | null) => setTrialDate(formatRunDate(date))}
          />
        </div>

        <div className="fieldGroup">
          <Dropdown
            label="Level *"
            placeholder="Select Level"
            options={levelOptions}
            value={runOption(levelOptions, level)}
            onChange={(_e, option) => setLevel(option.id)}
          />
        </div>

        <div className="fieldGroup">
          <Dropdown
            label="Result *"
            placeholder="Select Result"
            options={resultOptions}
            value={runOption(resultOptions, resultStatus)}
            onChange={(_e, option) => setResultStatus(option.id)}
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="Judge Number"
            id="judgeNumber"
            value={judgeNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeNumber(e.target.value)}
            placeholder="Optional"
          />
          <span className="fieldHelp">
            Preferred for uniqueness (e.g., AKC judge number)
          </span>
        </div>

        <div className="fieldGroup">
          <Input
            label="Judge Name"
            id="judgeName"
            value={judgeName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeName(e.target.value)}
            placeholder="Optional"
          />
          <span className="fieldHelp">
            Used as fallback if judge number not available
          </span>
        </div>

        <div className="fieldGroup">
          <Input
            label="Event Name"
            id="eventName"
            value={eventName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventName(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="City"
            id="city"
            value={city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="State"
            id="state"
            value={state}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setState(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="fieldGroup fullWidth">
          <TextArea
            className="runNotes"
            label="Notes"
            placeholder="Optional notes about this run"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="formActions">
        <Button
          onClick={onCancel}
          variant="secondary"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? "Saving..." : editRun ? "Update Run" : "Add Run"}
        </Button>
      </div>
    </form>
  );
};

export default FetchRunForm;

